/**
 * Shared mid-cycle (in-flight) FCR formula. Extracted from
 * `lotCostPerKg.ts` (v2.2 T3-B-06e) so a second surface (the Biomass
 * per-lot KPI card) can show the same number without reimplementing the
 * formula — one source of truth per KPI
 * (docs/architecture-concerns.md I-6).
 */

export type FeedConversionRatioBand = 'below-range' | 'in-range' | 'above-range' | 'unavailable'

/**
 * In-flight FCR = feed kg (to date) ÷ current biomass kg. Null when:
 *   - `totalFeedKgNum <= 0` (no feed → no ratio to compute).
 *   - `currentBiomassKg` is null, undefined, or non-positive (can't derive
 *     current biomass without stock + weight signals).
 *   - `hasSettledHarvest` is true — the settled (at-harvest) FCR is the
 *     truth after the accounting book closes; in-flight goes intentionally
 *     null so the operator's eye follows the settled number on completed
 *     lots.
 *
 * When null, the caller either has insufficient in-flight data OR the lot
 * has already been harvested. Never returns a lie.
 */
export const computeFcrInFlight = (
  totalFeedKgNum: number,
  currentBiomassKg: number | null | undefined,
  hasSettledHarvest: boolean
): string | null => {
  if (hasSettledHarvest) return null
  if (totalFeedKgNum <= 0) return null
  if (currentBiomassKg === null || currentBiomassKg === undefined) return null
  if (!Number.isFinite(currentBiomassKg) || currentBiomassKg <= 0) return null
  const ratio = totalFeedKgNum / currentBiomassKg
  if (!Number.isFinite(ratio)) return null
  return ratio.toFixed(2)
}

export const computeFcrInFlightBand = (args: {
  feedConversionRatioInFlight: string | null
  expectedFcrMin: number | null
  expectedFcrMax: number | null
}): FeedConversionRatioBand => {
  if (!args.feedConversionRatioInFlight) return 'unavailable'
  if (args.expectedFcrMin === null || args.expectedFcrMax === null) return 'unavailable'

  const value = Number(args.feedConversionRatioInFlight)
  if (!Number.isFinite(value)) return 'unavailable'
  if (value < args.expectedFcrMin) return 'below-range'
  if (value > args.expectedFcrMax) return 'above-range'
  return 'in-range'
}
